import { useEffect, useMemo, useRef } from 'react';
import { PlaneGeometry } from 'three';
import type { Material } from 'three';
import {
  ELEVATION_COURSE_DOWNHILL_END_X,
  ELEVATION_COURSE_ROAD_HALF_WIDTH,
  ELEVATION_COURSE_TERRAIN_HALF_WIDTH,
  ELEVATION_COURSE_UPHILL_START_X,
  sampleDrivingTerrain,
  type DrivingTerrainSample,
} from '../model/drivingTerrain';
import { DRIVING_FLOOR_RADIUS } from '../model/stageGround';
import type { RuntimeWeather } from '../model/weatherTransition';
import type { AsphaltTextures } from './materials/asphaltSurface';
import { useWeatherRoadMaterials } from './WeatherAppliers';

const TERRAIN_Y_OFFSET = -0.002;
const ROAD_Y_OFFSET = -0.0015;
const PAINT_Y_OFFSET = -0.0009;
const EDGE_LINE_WIDTH = 0.55;
const LENGTH_SEGMENTS = 256;
const TERRAIN_WIDTH_SEGMENTS = 288;

function createCourseGeometry(
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
  lengthSegments: number,
  widthSegments: number,
  yOffset: number
): PlaneGeometry {
  const width = maxX - minX;
  const depth = maxZ - minZ;
  const geometry = new PlaneGeometry(width, depth, lengthSegments, widthSegments);
  geometry.rotateX(-Math.PI / 2);
  geometry.translate((minX + maxX) / 2, 0, (minZ + maxZ) / 2);

  const positions = geometry.getAttribute('position');
  const uvs = geometry.getAttribute('uv');
  const sample: DrivingTerrainSample = { height: 0, slopeX: 0, slopeZ: 0 };
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const z = positions.getZ(index);
    sampleDrivingTerrain(x, z, sample);
    positions.setY(index, sample.height + yOffset);
    uvs.setXY(
      index,
      (x + DRIVING_FLOOR_RADIUS) / (DRIVING_FLOOR_RADIUS * 2),
      (-z + DRIVING_FLOOR_RADIUS) / (DRIVING_FLOOR_RADIUS * 2)
    );
  }
  positions.needsUpdate = true;
  uvs.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

export interface ElevationCourseProps {
  asphalt: AsphaltTextures | null;
  weather: RuntimeWeather;
}

/** 고저차 지형의 사면은 잔디, 중앙 24m 차선만 아스팔트로 분리해 렌더합니다. */
export function ElevationCourse({ asphalt, weather }: ElevationCourseProps) {
  const terrainSurface = useMemo(
    () =>
      createCourseGeometry(
        ELEVATION_COURSE_UPHILL_START_X,
        ELEVATION_COURSE_DOWNHILL_END_X,
        -ELEVATION_COURSE_TERRAIN_HALF_WIDTH,
        ELEVATION_COURSE_TERRAIN_HALF_WIDTH,
        LENGTH_SEGMENTS,
        TERRAIN_WIDTH_SEGMENTS,
        TERRAIN_Y_OFFSET
      ),
    []
  );
  const roadSurface = useMemo(
    () =>
      createCourseGeometry(
        ELEVATION_COURSE_UPHILL_START_X,
        ELEVATION_COURSE_DOWNHILL_END_X,
        -ELEVATION_COURSE_ROAD_HALF_WIDTH,
        ELEVATION_COURSE_ROAD_HALF_WIDTH,
        LENGTH_SEGMENTS,
        4,
        ROAD_Y_OFFSET
      ),
    []
  );
  const leftEdge = useMemo(
    () =>
      createCourseGeometry(
        ELEVATION_COURSE_UPHILL_START_X,
        ELEVATION_COURSE_DOWNHILL_END_X,
        -ELEVATION_COURSE_ROAD_HALF_WIDTH - EDGE_LINE_WIDTH / 2,
        -ELEVATION_COURSE_ROAD_HALF_WIDTH + EDGE_LINE_WIDTH / 2,
        LENGTH_SEGMENTS,
        1,
        PAINT_Y_OFFSET
      ),
    []
  );
  const rightEdge = useMemo(
    () =>
      createCourseGeometry(
        ELEVATION_COURSE_UPHILL_START_X,
        ELEVATION_COURSE_DOWNHILL_END_X,
        ELEVATION_COURSE_ROAD_HALF_WIDTH - EDGE_LINE_WIDTH / 2,
        ELEVATION_COURSE_ROAD_HALF_WIDTH + EDGE_LINE_WIDTH / 2,
        LENGTH_SEGMENTS,
        1,
        PAINT_Y_OFFSET
      ),
    []
  );

  useEffect(
    () => () => {
      terrainSurface.dispose();
      roadSurface.dispose();
      leftEdge.dispose();
      rightEdge.dispose();
    },
    [terrainSurface, roadSurface, leftEdge, rightEdge]
  );

  // 날씨 값은 프레임 루프에서 재질에 직접 씁니다(DrivingGround와 같은 이유).
  const roadMaterialRef = useRef<Material | Material[] | null>(null);
  const shoulderMaterialRef = useRef<Material | Material[] | null>(null);
  useWeatherRoadMaterials(weather, {
    asphalt: roadMaterialRef,
    ground: shoulderMaterialRef,
  });

  return (
    <>
      <mesh geometry={terrainSurface} receiveShadow={false}>
        <meshStandardMaterial ref={shoulderMaterialRef} color="#527346" roughness={0.96} metalness={0} />
      </mesh>

      <mesh geometry={roadSurface} receiveShadow={false}>
        {asphalt === null ? (
          <meshStandardMaterial ref={roadMaterialRef} color="#3a3d42" roughness={0.72} metalness={0.04} />
        ) : (
          <meshStandardMaterial
            ref={roadMaterialRef}
            map={asphalt.color}
            normalMap={asphalt.normal}
            roughnessMap={asphalt.roughness}
            roughness={0.72}
            metalness={0.04}
            envMapIntensity={0.35}
            polygonOffset
            polygonOffsetFactor={-1}
            polygonOffsetUnits={-1}
          />
        )}
      </mesh>

      {[leftEdge, rightEdge].map((geometry, index) => (
        <mesh key={index === 0 ? 'left-edge' : 'right-edge'} geometry={geometry}>
          <meshStandardMaterial
            color="#f0f0f2"
            roughness={0.62}
            metalness={0}
            polygonOffset
            polygonOffsetFactor={-2}
            polygonOffsetUnits={-2}
          />
        </mesh>
      ))}
    </>
  );
}
